import { useEffect, useState } from "react";

// Components
import TableLoader from "@Layouts/components/Skeletons/TableLoader";
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
import OemEmployees from "@DataPages/components/MyOEM/Tables/OemEmployees";
import OemRegionStore from "@DataPages/components/MyOEM/Tables/OemRegionStore";

// helpers
import { formatNumberWithCommas } from "@Common/helpers/Modifiers/formatters";

// MUI
import { Grid } from "@mui/material";

// Images
import emptyTable from "@/assets/images/commons/empty_table.png";
import { errorLogger } from "@/Common/helpers/logger";

const matchReferences = (references = []) => {
	const referencesMap = new Map();

	if (references.length) {
		references
			.filter((reference) => reference?.metrics?.length)
			.forEach((reference) => {
				const referenceData = referencesMap.get(reference._id) || {
					_id: reference._id,
				};

				reference?.metrics?.forEach((metric) => {
					referenceData[metric.metricName] = metric?.metricValue || 0.0;
				});

				delete referenceData?.metrics;
				delete referenceData?.user;
				referencesMap.set(reference._id, referenceData);
			});
	}

	return Array.from(referencesMap.values());
};

const getReferenceColumns = (references = null) => {
	const columns = [];
	if (references) {
		Object.keys(references).forEach((key) => {
			if (key) {
				const columnDef = {
					field: key,
					headerName: key,
					type: key === "Employee Name" ? "string" : "number",
					hide: Boolean(key === "_id" || key === "referenceType"),
					sortable: true,
					headerClassName: Boolean(key === "Employee Name")
						? "table-header-top-aligned table-small-header show-as-column"
						: "table-header-top-aligned table-small-header show-as-column header-text-right",
					cellClassName: `table-small-cell`,
					align: key === "Employee Name" ? "left" : "right",
					width: 180,
					flex: 1,
					renderCell: (param) => {
						if (param.value && isNaN(param.value)) return param.value;
						try {
							if (param.field === "# of Responses") {
								return formatNumberWithCommas(Number(param.value).toFixed(0).toString());
							} else {
								return formatNumberWithCommas(Number(param.value).toFixed(2).toString());
							}
						} catch (error) {
							errorLogger(error);
						}

						return "Unknown";
					},
				};

				columns.push(columnDef);
			}
		});
	}

	return columns;
};

const getEmployeeColumns = (employees = null) => {
	const columns = [];
	if (employees) {
		Object.keys(employees).forEach((key) => {
			const header = key === "responses" ? "# of Responses" : key === "responseRate" ? "Response Rate" : key;

			const columnDef = {
				field: key,
				headerName: header,
				hide: Boolean(key === "_id" || key === "referenceType"),
				sortable: false,
				type: key === "Employee Name" ? "string" : "number",
				headerClassName:
					key === "Employee Name"
						? "table-header-top-aligned table-small-header show-as-column"
						: "table-header-top-aligned table-small-header show-as-column header-text-right",
				cellClassName: `table-small-cell`,
				align: key === "Employee Name" ? "left" : "right",
				width: 150,
				flex: 1,
				renderCell: (param) => {
					if (param.value && isNaN(param.value)) {
						return param.value;
					}
					try {
						if (param.value) {
							if (param.field === "# of Responses") {
								return formatNumberWithCommas(Number(param.value).toFixed(0).toString());
							} else {
								return formatNumberWithCommas(Number(param.value).toFixed(2).toString());
							}
						}
						return Number(0).toFixed(2);
					} catch (error) {
						errorLogger(error);
					}

					return "Unknown";
				},
			};

			columns.push(columnDef);
		});

		let first = null;
		columns?.forEach((element, key) => {
			if (element.field === "Employee Name") {
				first = element;
				delete columns[key];
			}
		});

		if (first) {
			columns.unshift(first);
		}
	}

	return columns;
};

export default function OEMDataContent(props) {
	const [reference, setReference] = useState([]);
	const [referenceColumns, setReferenceColumns] = useState([]);
	const [employees, setEmployees] = useState([]);
	const [employeesColumns, setEmployeesColumns] = useState([]);

	useEffect(() => {
		let referenceToTable = [];
		let columnsReferenceToTable = [];
		let employeesToTable = [];
		let columnsEmployeesToTable = [];

		if (props.isDataPresent) {
			referenceToTable = matchReferences(props.referenceMetrics || []);
			employeesToTable = matchReferences(props.employeeMetrics || []);

			if (referenceToTable.length) {
				columnsReferenceToTable = getReferenceColumns(referenceToTable[0]);
			}
			if (employeesToTable.length) {
				columnsEmployeesToTable = getEmployeeColumns(employeesToTable[0]);
			}
		}

		setReference(referenceToTable);
		setEmployees(employeesToTable);
		setReferenceColumns(columnsReferenceToTable);
		setEmployeesColumns(columnsEmployeesToTable);

		return () => {
			setReference([]);
			setEmployees([]);
			setReferenceColumns([]);
			setEmployeesColumns([]);
		};
	}, [props]);

	return (
		<>
			{props.loading ? (
				<Grid container item mt={0} rowSpacing={2} columnSpacing={4}>
					<Grid item xs={12}>
						<TableLoader content rows={2} />
					</Grid>
					<Grid item mt={2} xs={12}>
						<TableLoader content rows={7} toolbar buttons={2} />
					</Grid>
				</Grid>
			) : (
				<>
					{props.isDataPresent ? (
						<Grid container item mt={0} rowSpacing={2}>
							<Grid item xs={12} sx={{ minWidth: "100px" }}>
								<OemRegionStore columns={referenceColumns || []} data={reference || []} />
							</Grid>

							{Boolean(employees?.length) && (
								<Grid item xs={12} sx={{ minWidth: "100px" }}>
									<OemEmployees columns={employeesColumns} data={employees || []} />
								</Grid>
							)}
						</Grid>
					) : (
						<EmptyBody image={emptyTable} title={"No Performance found for this month"} bodyHeight={"480px"} />
					)}
				</>
			)}
		</>
	);
}
